<!-- Renders a Mermaid flowchart of business entities and their relationships. -->
<!-- Uses dynamic import for mermaid and panzoom for large graph navigation. -->
<template>
    <div ref="containerRef" class="entities-diagram"></div>
    <Popup
        v-for="entity in entities"
        :key="entity.identity"
        :anchor="`#entity-node-${entity.identity}`"
    >
        <GenericCard
            :entityId="entity.identity"
            :entityType="EntityType.BUSINESS_ENTITY"
            :showPath="false"
        />
    </Popup>
</template>

<script setup lang="ts">
import panzoom from "panzoom";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { BusinessEntity } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import { useUIManager } from "@/stores/ui-store";

const props = defineProps<{
    entities: BusinessEntity[];
}>();

const containerRef = ref<HTMLElement | null>(null);
const { darkMode } = useUIManager();
const route = useRoute();
const router = useRouter();

const mermaidSource = computed(() => {
    const nodeId = (id: string) => id.replaceAll("-", "_");
    const lines = ["flowchart LR"];

    for (const entity of props.entities) {
        lines.push(`    ${nodeId(entity.identity)}["${entity.name}"]`);
    }

    for (const entity of props.entities) {
        for (const rel of entity.relationships) {
            if (rel.direction === "outgoing") {
                lines.push(
                    `    ${nodeId(entity.identity)} -->|"${rel.type}"| ${nodeId(rel.other_id)}`
                );
            }
        }
    }

    return lines.join("\n");
});

const renderDiagram = async () => {
    const container = containerRef.value;
    if (!container) return;

    try {
        const mermaid = (await import("mermaid")).default;

        const cssVar = (name: string) =>
            window
                .getComputedStyle(document.body)
                .getPropertyValue(name)
                .trim();

        mermaid.initialize({
            startOnLoad: false,
            theme: "base",
            securityLevel: "strict",
            fontFamily: "inherit",
            suppressErrorRendering: true,
            themeVariables: darkMode.value
                ? {
                      background: cssVar("--surface-background"),
                      primaryColor: cssVar("--stekz-primary-color"),
                      primaryTextColor: cssVar("--stekz-white"),
                      primaryBorderColor: "#00000000",
                      lineColor: cssVar("--palette-purple-200"),
                      secondaryColor: cssVar("--palette-pink-600"),
                      edgeLabelBackground: cssVar("--surface-background"),
                  }
                : {
                      background: cssVar("--surface-background-mild"),
                      primaryColor: cssVar("--stekz-white"),
                      primaryTextColor: cssVar("--palette-grey-500"),
                      primaryBorderColor: "#00000000",
                      lineColor: cssVar("--text-color"),
                      secondaryColor: cssVar("--stekz-white"),
                      edgeLabelBackground: cssVar("--stekz-white"),
                  },
        });

        const parseResult = await mermaid.parse(mermaidSource.value, {
            suppressErrors: true,
        });

        if (!parseResult) {
            throw new Error("Invalid Mermaid diagram syntax");
        }

        const id = `mermaid-${Math.random().toString(36).substring(2, 11)}`;
        const { svg } = await mermaid.render(id, mermaidSource.value);

        const diagram = document.createElement("div");
        diagram.innerHTML = svg;

        // Add a bottom border line to each node, matching the entity-card border-bottom.
        diagram
            .querySelectorAll<SVGRectElement>(".node rect")
            .forEach((rect) => {
                const x = parseFloat(rect.getAttribute("x") ?? "0");
                const y = parseFloat(rect.getAttribute("y") ?? "0");
                const w = parseFloat(rect.getAttribute("width") ?? "0");
                const h = parseFloat(rect.getAttribute("height") ?? "0");

                const line = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "line"
                );
                line.setAttribute("x1", String(x));
                line.setAttribute("y1", String(y + h));
                line.setAttribute("x2", String(x + w));
                line.setAttribute("y2", String(y + h));
                line.setAttribute("class", "node-bottom-border");
                rect.parentElement?.appendChild(line);
            });

        // Build a lookup from Mermaid node id → entity identity.
        const nodeIdToEntityId = new Map(
            props.entities.map((e: BusinessEntity) => [
                e.identity.replaceAll("-", "_"),
                e.identity,
            ])
        );

        // Wire up click navigation and stamp ids for Popup anchors on each node.
        diagram.querySelectorAll<SVGGElement>(".node").forEach((nodeEl) => {
            const svgId = nodeEl.getAttribute("id") ?? "";
            // Mermaid sets id="flowchart-{nodeId}-{N}"
            const match = svgId.match(/^flowchart-(.+)-\d+$/);
            if (!match) return;

            const entityId = nodeIdToEntityId.get(match[1]);
            if (!entityId) return;

            // Stamp a stable id so the Popup selector anchor can find this element.
            nodeEl.setAttribute("id", `entity-node-${entityId}`);

            // Distinguish a real click from a pan gesture by tracking drag distance.
            let dragStartX = 0;
            let dragStartY = 0;
            nodeEl.addEventListener("mousedown", (e) => {
                dragStartX = e.clientX;
                dragStartY = e.clientY;
            });
            nodeEl.addEventListener("click", (e) => {
                const dx = e.clientX - dragStartX;
                const dy = e.clientY - dragStartY;
                if (dx * dx + dy * dy > 25) return; // >5px movement = pan, not click
                router.push({
                    name: "businessEntityPage",
                    params: {
                        orgName: route.params.orgName,
                        stekName: route.params.stekName,
                        businessEntityId: entityId,
                    },
                });
            });
        });

        container.innerHTML = "";
        container.appendChild(diagram);

        const panzoomInstance = panzoom(diagram, {
            beforeWheel: (e) => !(e.metaKey || e.ctrlKey),
            zoomDoubleClickSpeed: 1,
        });

        // Center the diagram after layout is painted
        requestAnimationFrame(() => {
            const svgEl = diagram.querySelector("svg");
            if (!svgEl) return;
            const containerRect = container.getBoundingClientRect();
            const svgRect = svgEl.getBoundingClientRect();
            panzoomInstance.moveTo(
                Math.max(0, (containerRect.width - svgRect.width) / 2),
                Math.max(0, (containerRect.height - svgRect.height) / 2)
            );
        });
    } catch (error) {
        console.error("Failed to render diagram:", error);
        container.innerHTML = `<p class="diagram-error">Failed to render diagram: ${error}</p>`;
    }
};

onMounted(renderDiagram);

watch(() => props.entities, renderDiagram, { deep: true });
watch(darkMode, renderDiagram);
</script>

<style scoped lang="scss">
.entities-diagram {
    overflow: hidden;
    width: 100%;
    min-height: 300px;
    border: 1px solid var(--p-surface-200);

    // Assign the browser default background
    --shadow-color: color-mix(in srgb, var(--text-color) 24%, transparent);

    :deep(svg) {
        width: 100%;
        height: auto;
    }

    // Match entity-card shadow and hover scale.
    // transform-box: fill-box ensures scale(1.01) expands from the node's own center,
    // not the SVG viewport origin.
    :deep(.node) {
        cursor: pointer;
        filter: drop-shadow(0 1px 3px var(--shadow-color));
        transform-box: fill-box;
        transform-origin: center;
        transition:
            filter 0.25s ease,
            transform 0.25s ease;
    }

    // Bottom border matching entity-card's border-bottom
    :deep(.node-bottom-border) {
        stroke: var(--stekz-primary-color);
        stroke-width: var(--stekz-border-width);
        pointer-events: none;
    }

    // Darken node background on hover, matching entity-card hover state.
    // !important needed to override Mermaid's inline fill attributes.
    :deep(.node rect),
    :deep(.node circle),
    :deep(.node ellipse),
    :deep(.node polygon) {
        transition: fill 0.25s ease;
    }

    :deep(.node:hover rect),
    :deep(.node:hover circle),
    :deep(.node:hover ellipse),
    :deep(.node:hover polygon) {
        fill: var(--surface-background-mild) !important;
    }
}

.diagram-error {
    color: var(--p-red-500);
    padding: 1rem;
}
</style>
